'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.0.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Init off-canvas after mobile menu is ready
		document.addEventListener('mobilemenu-init-done', () => {
			const offCanvas = new OffCanvas({
				debug: false,
				overlay: true
			});
		});

		// Init mobile menu
		const mobileMenu = new MobileMenu();
		mobileMenu.init();

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			...options
		};

		// Init cache
		this.cache = {};

		// Call individual init methods
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initHero();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	 * Initialize image links
	 *
	 * Find all links to images and use GLightbox to display them.
	 */
	initImageLinks() {
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);

		if (!parentNode) return;

		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			let figcaption = link.parentNode.querySelector('figcaption');
			let caption = figcaption ? figcaption.textContent : '';

			link.addEventListener('click', e => {
				e.preventDefault();

				const lightbox = GLightbox();
				lightbox.setElements([
					{
						href: link.href,
						type: 'image',
						title: caption
					}
				]);

				lightbox.open();
			});
		});
	}

	/**
	 * Initialize hero carousel
	 */
	initHero() {
		this.cache.heroContainer = document.getElementById('js-hero-container');
		if (this.cache.heroContainer) {
			this.cache.heroes = this.cache.heroContainer.querySelectorAll('.hero');
			if (this.cache.heroes.length > 0) {

				// set hero item heights
				this.updateHeroHeight();
				let heroHeightTimeout = null;
				window.addEventListener('resize', () => {
					window.clearTimeout(heroHeightTimeout);
					heroHeightTimeout = window.setTimeout(() => {
						this.updateHeroHeight();
					}, 150);
				});

				// autoplay button
				const autoplayButton = document.createElement('button');
				autoplayButton.classList.add('hero__autoplay');
				this.cache.heroContainer.parentNode.insertBefore(autoplayButton, this.cache.heroContainer);

				// init slider (tiny-slider)
				const slider = tns({
					container: '#js-hero-container',
					items: 1,
					slideBy: 'page',
					nav: false,
					controls: true,
					controlsText: [
						'<i class="icon-left-open" aria-hidden="true"></i>',
						'<i class="icon-right-open" aria-hidden="true"></i>'
					],
					mode: 'gallery',
					autoplayTimeout: 6500,
					speed: 1000,
					autoplay: true,
					autoplayButton: autoplayButton,
					autoplayButtonOutput: true,
					autoplayText: [
						'<i class="icon-play" aria-hidden="true"></i>',
						'<i class="icon-pause" aria-hidden="true"></i>'
					]
				});
			}
		}
	}

	/**
	 * Set hero heights
	 */
	updateHeroHeight() {
		let minHeight = 0;
		this.cache.heroes.forEach(hero => {
			hero.removeAttribute('style');
			if (minHeight < hero.offsetHeight) {
				minHeight = hero.offsetHeight;
			}
		});
		this.cache.heroes.forEach(hero => {
			hero.style = 'min-height: ' + minHeight + 'px';
		});
	}
}

window._site = new Site();
